import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyCard from '../components/CompanyCard';
import { popularCompanies } from '../data/popularCompanies';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';

interface CompanyDirectoryProps {
  darkMode: boolean;
}

const CompanyDirectory: React.FC<CompanyDirectoryProps> = ({ darkMode }) => {
  const navigate = useNavigate();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'rating' | 'salary'>('name');
  const [showFilters, setShowFilters] = useState(false);

  const industries = useMemo(() => {
    const set = new Set(popularCompanies.map(c => c.industry));
    return Array.from(set).sort();
  }, []);

  const filtered = useMemo(() => {
    let result = [...popularCompanies];

    if (search) {
      const q = search.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.tags.some(t => t.toLowerCase().includes(q))
      );
    }

    if (industry) {
      result = result.filter(c => c.industry === industry);
    }

    result.sort((a, b) => {
      if (sortBy === 'name') return a.name.localeCompare(b.name);
      if (sortBy === 'rating') return b.glassdoor_rating - a.glassdoor_rating;
      return b.salary_range.max - a.salary_range.max;
    });

    return result;
  }, [search, industry, sortBy]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
  };

  return (
    <div className={`page-container ${darkMode ? 'dark' : ''}`}>
      <div className="browse-header">
        <h1>Company Directory</h1>
        <p className="browse-subtitle">{filtered.length} companies with Glassdoor ratings and salary data</p>
      </div>

      <div className="browse-controls">
        <form className="search-bar" onSubmit={handleSearch}>
          <SearchIcon sx={{ fontSize: 20, color: '#888' }} />
          <input
            type="text"
            placeholder="Search companies by name or tag..."
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <button type="submit" className="btn-search">Search</button>
        </form>

        <button
          className="btn-filter-toggle"
          onClick={() => setShowFilters(!showFilters)}
        >
          <TuneIcon sx={{ fontSize: 18 }} />
          Filters
        </button>
      </div>

      {showFilters && (
        <div className="filter-panel">
          <div className="filter-row">
            <div className="filter-group">
              <label>Industry</label>
              <select value={industry} onChange={e => setIndustry(e.target.value)}>
                <option value="">All Industries</option>
                {industries.map(i => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>Sort By</label>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as any)}>
                <option value="name">Name A-Z</option>
                <option value="rating">Highest Rating</option>
                <option value="salary">Highest Salary</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <h3>No companies found</h3>
          <p>Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="company-grid">
          {filtered.map(c => (
            <CompanyCard
              key={c.id}
              company={c}
              onClick={(id) => navigate(`/companies/${id}`)}
              darkMode={darkMode}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default CompanyDirectory;
